import dayjs from 'dayjs'
import SquarePaymentsStore from './store/square-payments'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faCircleXmark,
  faHardDrive,
  faLocationCrosshairs,
  faTriangleExclamation,
  faCheckCircle,
  faTimesCircle,
  faCreditCard,
  faBan,
  faCircleCheck,
  faCircleQuestion,
  faBatteryFull,
  faBatteryThreeQuarters,
  faBatteryHalf,
  faBatteryQuarter,
  faBatteryEmpty,
  faWifi,
  faXmark,
  faEthernet,
  faTrash,
  faInfoCircle,
  faTerminal,
  faClock,
  faEdit,
  faArrowRotateRight,
  faLocationDot,
  faCircleExclamation,
  faHourglassHalf
} from '@fortawesome/free-solid-svg-icons'

import {
  SquarePaymentError,
  SquareServiceError,
  SquareErrorList
} from './components/square/errors.js'

// Dynamic imports with extensibility wrapper
const createAsyncComponent = (importFn) => {
  return () => importFn().then(module => {
    const component = module.default || module
    // Ensure the component object remains extensible
    if (component && typeof component === 'object' && !Array.isArray(component)) {
      // Create a new extensible object to avoid any potential freezing issues
      const extensibleComponent = Object.assign({}, component)
      // Ensure the component is extensible (should be by default, but being explicit)
      return extensibleComponent
    }
    return component
  })
}

const SquarePaymentsOAuth2Connect = createAsyncComponent(() => import('./components/square/oauth/SquarePaymentsOAuth2Connect.vue'))
const SquarePaymentsOAuth2Redirect = createAsyncComponent(() => import('./components/square/oauth/SquarePaymentsOAuth2Redirect.vue'))
const SquarePaymentOAuth2ServiceConnected = createAsyncComponent(() => import('./components/square/oauth/SquarePaymentOAuth2ServiceConnected.vue'))
const SquarePaymentsOAuth2ConnectSuccess = createAsyncComponent(() => import('./components/square/oauth/SquarePaymentsOAuth2ConnectSuccess.vue'))
const SquarePaymentsOAuth2ConnectFailure = createAsyncComponent(() => import('./components/square/oauth/SquarePaymentsOAuth2ConnectFailure.vue'))

const SquarePaymentsCardPayment = createAsyncComponent(() => import('./components/square/cards/SquarePaymentsCardPayment.vue'))
const SquarePaymentsTerminalPayment = createAsyncComponent(() => import('./components/square/terminal/SquarePaymentsTerminalPayment.vue'))
const SquarePaymentsStoreCard = createAsyncComponent(() => import('./components/square/customer/SquarePaymentsStoreCard.vue'))

const SquarePaymentsPaymentError = createAsyncComponent(() => import('./components/square/modals/content/payment-error.vue'))
const SquarePaymentsPaymentSuccess = createAsyncComponent(() => import('./components/square/modals/content/payment-success.vue'))
const SquarePaymentsPaymentFailure = createAsyncComponent(() => import('./components/square/modals/content/payment-failure.vue'))
const SquarePaymentsPaymentTimeout = createAsyncComponent(() => import('./components/square/modals/content/payment-timeout.vue'))

const SquarePaymentsStorecardError = createAsyncComponent(() => import('./components/square/modals/content/storecard-error.vue'))
const SquarePaymentsStorecardSuccess = createAsyncComponent(() => import('./components/square/modals/content/storecard-success.vue'))
const SquarePaymentsStorecardFailure = createAsyncComponent(() => import('./components/square/modals/content/storecard-failure.vue'))

const SquarePaymentsListStoredCards = createAsyncComponent(() => import('./components/square/customer/SquarePaymentsListAccountStoredCards.vue'))
const SquarePaymentsStoredcardDisplay = createAsyncComponent(() => import('./components/square/components/credit-card-display.vue'))
const SquarePaymentsSelectUserStoredCard = createAsyncComponent(() => import('./components/square/customer/SquarePaymentsSelectUserStoredCard.vue'))

const SquareLocationList = createAsyncComponent(() => import('./components/square/terminal/Locations/SquareLocationList.vue'))

const SquareTerminalDeviceList = createAsyncComponent(() => import('./components/square/terminal/Devices/SquareTerminalDeviceList.vue'))
const SquareTerminalDeviceCodesList = createAsyncComponent(() => import('./components/square/terminal/Devices/SquareTerminalDeviceCodesList.vue'))
const SquareTerminalConnectionManager = createAsyncComponent(() => import('./components/square/terminal/SquareTerminalConnectionManager.vue'))

const LocationsByTxnTypeConfig = createAsyncComponent(() => import('./components/square/config/LocationsByTxnTypeConfig.vue'))

// Default values for development mode when placeholders aren't replaced
const getVersionInfo = () => {
  // Check if build placeholders have been replaced by Rollup
  const buildDate = typeof __buildDate__ === 'string' && __buildDate__ !== '__buildDate__'
    ? __buildDate__
    : dayjs().format('DD MMM YYYY - HH:mm')

  const buildVersion = typeof __buildVersion__ === 'string' && __buildVersion__ !== '__buildVersion__'
    ? __buildVersion__
    : 'v1.0.0-dev'

  return { buildDate, buildVersion }
}

const install = (Vue, options) => {
  if (!options.store) {
    throw new Error('Please initalize the Square Payments with a vuex store.')
  }

  // Log build information when component is installed
  if (typeof console !== 'undefined' && console.debug) {
    const { buildVersion, buildDate } = getVersionInfo()
    console.debug('Square Payments Components Library ' + buildVersion + ' (built ' + buildDate + ')')
  }

  options.store.registerModule('square', SquarePaymentsStore)

  library.add(
    faBan,
    faCheckCircle,
    faTimesCircle,
    faCreditCard,
    faTriangleExclamation,
    faCircleXmark,
    faCheckCircle,
    faLocationCrosshairs,
    faHardDrive,
    // New icons for device details
    faCircleCheck,
    faCircleQuestion,
    faBatteryFull,
    faBatteryThreeQuarters,
    faBatteryHalf,
    faBatteryQuarter,
    faBatteryEmpty,
    faWifi,
    faXmark,
    faEthernet,
    faTrash,
    faInfoCircle,
    faTerminal,
    faClock,
    faEdit,
    faArrowRotateRight,
    faLocationDot,
    faCircleExclamation,
    faHourglassHalf
  )

  Vue.component('square-payments-oauth2-connect', SquarePaymentsOAuth2Connect)
  Vue.component('square-payments-oauth2-redirect', SquarePaymentsOAuth2Redirect)
  Vue.component('square-payments-oauth2-service-connected', SquarePaymentOAuth2ServiceConnected)
  Vue.component('square-payments-oauth2-connect-success', SquarePaymentsOAuth2ConnectSuccess)
  Vue.component('square-payments-oauth2-connect-failure', SquarePaymentsOAuth2ConnectFailure)

  Vue.component('square-payment-error', SquarePaymentsPaymentError)
  Vue.component('square-payment-failure', SquarePaymentsPaymentFailure)
  Vue.component('square-payment-success', SquarePaymentsPaymentSuccess)
  Vue.component('square-payment-timeout', SquarePaymentsPaymentTimeout)

  Vue.component('square-storecard-error', SquarePaymentsStorecardError)
  Vue.component('square-storecard-failure', SquarePaymentsStorecardFailure)
  Vue.component('square-storecard-success', SquarePaymentsStorecardSuccess)

  Vue.component('square-payments-card-payment', SquarePaymentsCardPayment)
  Vue.component('square-payments-terminal-payment', SquarePaymentsTerminalPayment)

  Vue.component('square-payments-store-card', SquarePaymentsStoreCard)
  Vue.component('square-payments-list-stored-cards', SquarePaymentsListStoredCards)
  Vue.component('square-payments-storedcard-display', SquarePaymentsStoredcardDisplay)
  Vue.component('square-payments-user-stored-card-chooser', SquarePaymentsSelectUserStoredCard)

  if (process.env.SERVER_BUILD !== 'ssr') {
    Vue.component('square-location-list', SquareLocationList)

    Vue.component('square-terminal-device-list', SquareTerminalDeviceList)
    Vue.component('square-terminal-device-codes-list', SquareTerminalDeviceCodesList)
    Vue.component('square-terminal-connection-manager', SquareTerminalConnectionManager)

    Vue.component('square-locations-by-txntype-config', LocationsByTxnTypeConfig)
    Vue.component('settings-square-location-overrides', LocationsByTxnTypeConfig)
  }

  Vue.filter('longdate', (data) => {
    try {
      return dayjs(data).format('dddd, D MMM YYYY HH:mm')
    }
    catch (err) {
      return '--'
    }
  })

  Vue.filter('shortdate', (data) => {
    try {
      return dayjs(data).format('DD/MM/YYYY HH:mm')
    }
    catch (err) {
      return '--'
    }
  })

  Vue.filter('formatCurrency', (data, currency) => {
    let currencyCode = currency || 'AUD'
    let amount = 0
    try {
      if (typeof data === 'object' && data.currency) {
        currencyCode = data.currency
      }
      if (typeof data === 'object' && data.amount) {
        amount = data.amount
      }
      else {
        amount = data
      }
      amount = parseFloat(amount).toFixed(2)
      return `${currencyCode}$ ${amount}`
    }
    catch (err) {
      amount = 0
    }
    return `${currencyCode}$ ${amount}`
  })

  Vue.filter('uuidShort', (data) => {
    try {
      return data.split('-')[0]
    }
    catch (err) {
      return '--'
    }
  })
}

// Export individual components for tree shaking
export {
  SquarePaymentsOAuth2Connect,
  SquarePaymentsOAuth2Redirect,
  SquarePaymentOAuth2ServiceConnected,
  SquarePaymentsOAuth2ConnectSuccess,
  SquarePaymentsOAuth2ConnectFailure,
  SquarePaymentsCardPayment,
  SquarePaymentsTerminalPayment,
  SquarePaymentsStoreCard,
  SquarePaymentsPaymentError,
  SquarePaymentsPaymentSuccess,
  SquarePaymentsPaymentFailure,
  SquarePaymentsStorecardError,
  SquarePaymentsStorecardFailure,
  SquarePaymentsStorecardSuccess,
  SquarePaymentsListStoredCards,
  SquarePaymentsStoredcardDisplay,
  SquarePaymentsSelectUserStoredCard,
  SquareLocationList,
  SquareTerminalDeviceList,
  SquareTerminalDeviceCodesList,
  SquareTerminalConnectionManager,
  LocationsByTxnTypeConfig,
  SquarePaymentError,
  SquareServiceError,
  SquareErrorList,
  SquarePaymentsStore,
  install
}

// Default export for Vue.use()
export default {
  install,
  // Also export components for convenience
  SquarePaymentsOAuth2Connect,
  SquarePaymentsOAuth2Redirect,
  SquarePaymentOAuth2ServiceConnected,
  SquarePaymentsOAuth2ConnectSuccess,
  SquarePaymentsOAuth2ConnectFailure,
  SquarePaymentsCardPayment,
  SquarePaymentsTerminalPayment,
  SquarePaymentsStoreCard,
  SquarePaymentsPaymentError,
  SquarePaymentsPaymentSuccess,
  SquarePaymentsPaymentFailure,
  SquarePaymentsStorecardError,
  SquarePaymentsStorecardFailure,
  SquarePaymentsStorecardSuccess,
  SquarePaymentsListStoredCards,
  SquarePaymentsStoredcardDisplay,
  SquarePaymentsSelectUserStoredCard,
  SquareLocationList,
  SquareTerminalDeviceList,
  SquareTerminalDeviceCodesList,
  SquareTerminalConnectionManager,
  LocationsByTxnTypeConfig,
  SquarePaymentError,
  SquareServiceError,
  SquareErrorList,
  SquarePaymentsStore
}
